<template>
    <v-dialog v-model="unauthorisedErrorPopup" persistent width="400">
        <v-card>
            <v-card-title class="text-h5">
                Ooops ! You are unauthorized !!
            </v-card-title>
            <v-card-text>{{ errorMessage }}</v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="unauthorisedErrorPopup = false">
                    Okay
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <div class="login-container">
        <div class="header-wrapper">
            <img :src="require('@/assets/icons/commsai-logo.svg')" />
        </div>
        <div class="login-form-wrapper">
            <v-form v-model="isFormValid" @submit.prevent="emailPasswordLogin" class="form-container">
                <h3>Login</h3>
                <div class="input-wrapper">
                    <div class="input-label">Email<span class="red-color">*</span></div>
                    <v-text-field v-model="email" :rules="emailRules" placeholder="Enter Email" variant="outlined"
                        density="compact"></v-text-field>
                </div>
                <div class="input-wrapper">
                    <div class="input-label">Password<span class="red-color">*</span></div>
                    <v-text-field v-model="password" :rules="passwordRules" placeholder="Enter password"
                        variant="outlined" density="compact" :type="showPassword ? 'text' : 'password'">
                        <template #append-inner v-if="password">
                            <img :src="showPassword ? ShowPasswordIcon : HidePasswordIcon"
                                @click="updateShowPassword"></img>
                        </template>
                    </v-text-field>
                </div>

                <button class="login-btn" :disabled="!isFormValid || emailLoginLoader" @click="emailPasswordLogin">
                    Login
                    <v-progress-circular v-if="emailLoginLoader" indeterminate :size="18"
                        :width="2"></v-progress-circular>
                </button>
            </v-form>

            <div class="or-container">
                <div class="line"></div>
                <span>OR</span>
                <div class="line"></div>
            </div>

            <div class="login-btn-container">
                <button class="google-btn" :disabled="googleLoginLoader" @click="loginWithGoogle">
                    <img v-if="!googleLoginLoader" :src="require('@/assets/icons/google.svg')" alt="google" />
                    <v-progress-circular v-else indeterminate :size="18" :width="2"></v-progress-circular>
                </button>

                <button class="microsoft-btn" :disabled="microsoftLoginLoader" @click="loginWithMicrosoft">
                    <img v-if="!microsoftLoginLoader" :src="require('@/assets/icons/microsoft.svg')" alt="microsoft" />
                    <v-progress-circular v-else indeterminate :size="18" :width="2"></v-progress-circular>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import HidePasswordIcon from '@/assets/icons/hide-password.svg';
import ShowPasswordIcon from '@/assets/icons/show-password.svg';
import Cookies from 'js-cookie';
import axios from 'axios';
import {
    signInWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
    OAuthProvider,
    fetchSignInMethodsForEmail,
    linkWithCredential
} from 'firebase/auth';
import { auth } from '@/config/firebase';
import { useStore } from 'vuex';
import axiosInstance from '@/axiosInterceptor';

export default {
    data() {
        return {
            HidePasswordIcon,
            ShowPasswordIcon,
            isFormValid: false,
            email: '',
            password: '',
            showPassword: false,
            emailRules: [
                (v) => !!v || 'Email is required',
                (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Email must be valid',
            ],
            passwordRules: [
                v => !!v || 'Password is required.',
            ],
            emailLoginLoader: false,
            googleLoginLoader: false,
            microsoftLoginLoader: false,
            unauthorisedErrorPopup: false,
            errorMessage: 'You are unauthorized! Please login with authorized account',
            userDetails: null,
            visitedModules: [],
            googleProvider: new GoogleAuthProvider(),
            microsoftProvider: new OAuthProvider('microsoft.com')
        }
    },
    mounted() {
        
    },
    methods: {
        updateShowPassword() {
            this.showPassword = !this.showPassword;
        },
        async fetchUserDetailsAndRedirect() {
            try {
                const response = await axiosInstance.get('/user');
                this.userDetails = response.data.data;
                this.visitedModules = this.userDetails?.visitedModules || [];
                this.$store.dispatch('updateVisitedModules', this.visitedModules);
                await this.$router.push({ name: 'NarrativeHome' });
            } catch (error) {
                this.errorMessage = 'Failed to fetch user details.';
                this.unauthorisedErrorPopup = true;
            }
        },
        async sendTokenToBackend(firebaseToken, provider) {
            try {
                const response = await axios.post(
                    process.env.VUE_APP_PYTHON_BACKEND_URL + '/api/v1/user/login',
                    { token: firebaseToken, provider }
                );

                if (response.status === 200) {
                    await this.fetchUserDetailsAndRedirect();
                } else {
                    this.errorMessage = 'Login failed. Please try again.';
                    this.unauthorisedErrorPopup = true;
                }
            } catch (error) {
                this.errorMessage =
                    error.response?.data?.detail?.message ||
                    error.response?.data?.message ||
                    'You are unauthorized! Please login with authorized account';
                this.unauthorisedErrorPopup = true;
            }
        },

        async emailPasswordLogin() {
            this.emailLoginLoader = true;
            try {
                const userCredential = await signInWithEmailAndPassword(auth, this.email, this.password);
                const token = await userCredential.user.getIdToken();
                await this.sendTokenToBackend(token, 'email');
            } catch (error) {
                this.errorMessage = error.message || 'Invalid email or password';
                this.unauthorisedErrorPopup = true;
            } finally {
                this.emailLoginLoader = false;
            }
        },

        async loginWithGoogle() {
            this.googleLoginLoader = true;
            try {
                const result = await signInWithPopup(auth, this.googleProvider);
                const token = await result.user.getIdToken();
                await this.sendTokenToBackend(token, 'google');
            } catch (error) {

                if (error.code === 'auth/account-exists-with-different-credential') {
                    try {
                        const email = error.customData.email;
                        const pendingCred = GoogleAuthProvider.credentialFromError(error);

                        // Check existing sign-in methods
                        const methods = await fetchSignInMethodsForEmail(auth, email);

                        if (methods.includes('password')) {
                            // Prompt user for password
                            const password = prompt(`An account with ${email} already exists. Please enter your password to link with Google:`);

                            if (password) {
                                // Sign in with email/password first
                                const userCredential = await signInWithEmailAndPassword(auth, email, password);

                                // Now link the Google credential
                                await linkWithCredential(userCredential.user, pendingCred);

                                // Get fresh token after linking
                                const token = await userCredential.user.getIdToken(true);
                                await this.sendTokenToBackend(token, 'google');

                                alert("Google account successfully linked! You can now sign in with Google.");
                            } else {
                                this.errorMessage = 'Password is required to link accounts';
                                this.unauthorisedErrorPopup = true;
                            }
                        } else {
                            this.errorMessage = `This email is already linked with: ${methods.join(', ')}`;
                            this.unauthorisedErrorPopup = true;
                        }
                    } catch (linkError) {
                        this.errorMessage = linkError.message || 'Failed to link accounts';
                        this.unauthorisedErrorPopup = true;
                    }
                } else {
                    this.errorMessage = error.message || 'Google login failed';
                    this.unauthorisedErrorPopup = true;
                }
            } finally {
                this.googleLoginLoader = false;
            }
        },

        async loginWithMicrosoft() {
            this.microsoftLoginLoader = true;
            try {
                const result = await signInWithPopup(auth, this.microsoftProvider);
                const token = await result.user.getIdToken();
                await this.sendTokenToBackend(token, 'microsoft');
            } catch (error) {
                console.error("Microsoft login error:", error);
                if (error.code === 'auth/account-exists-with-different-credential') {
                    try {
                        const email = error.customData.email;
                        const pendingCred = OAuthProvider.credentialFromError(error);

                        // Check existing sign-in methods
                        const methods = await fetchSignInMethodsForEmail(auth, email);

                        if (methods.includes('password')) {
                            const password = prompt(`An account with ${email} already exists. Please enter your password to link with Microsoft:`);

                            if (password) {
                                // Sign in with email/password first
                                const userCredential = await signInWithEmailAndPassword(auth, email, password);

                                // Now link the Microsoft credential
                                await linkWithCredential(userCredential.user, pendingCred);

                                // Get fresh token after linking
                                const token = await userCredential.user.getIdToken(true);
                                await this.sendTokenToBackend(token, 'microsoft');

                                alert("Microsoft account successfully linked! You can now sign in with Microsoft.");
                            } else {
                                this.errorMessage = 'Password is required to link accounts';
                                this.unauthorisedErrorPopup = true;
                            }
                        } else {
                            this.errorMessage = `This email is already linked with: ${methods.join(', ')}`;
                            this.unauthorisedErrorPopup = true;
                        }
                    } catch (linkError) {
                        console.error("Error linking accounts:", linkError);
                        this.errorMessage = linkError.message || 'Failed to link accounts';
                        this.unauthorisedErrorPopup = true;
                    }
                } else {
                    this.errorMessage = error.message || 'Microsoft login failed';
                    this.unauthorisedErrorPopup = true;
                }
            } finally {
                this.microsoftLoginLoader = false;
            }
        }
    }
};
</script>

<style scoped>
.login-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 1.5em;
    font-family: 'Inter', sans-serif;
}

.header-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
}

.login-form-wrapper {
    width: 100%;
    max-width: 560px;
    display: flex;
    flex-direction: column;
    padding: 3em;
    gap: 2em;
    background-color: white;
    border-radius: 12px;
}

.form-container {
    display: flex;
    flex-direction: column;
    gap: 1em;
    color: #171717;
}

.input-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.red-color {
    color: #FF0A19;
}

.login-btn {
    display: flex;
    justify-content: center;
    align-items: center;
    background: linear-gradient(86.34deg, #FF00C3 7.5%, #FF0A19 50.5%, #FF7800 96%);
    border-radius: 8px;
    padding: 0.8em 1em;
    color: white;
    gap: 8px;
}

.or-container {
    display: flex;
    justify-content: center;
    width: 100%;
    align-items: center;
}

.or-container span {
    color: #D4D4D4;
    margin: 0px 8px;
}

.line {
    border: 1px solid #D4D4D4;
    height: 1px;
    width: 100%;
}

.login-btn-container {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
}

.google-btn,
.microsoft-btn {
    border: 1px solid #DADCE0;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    padding: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.google-btn img,
.microsoft-btn img {
    width: 100%;
    height: 100%;
}
</style>
